import React, { useCallback, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Modal from '@/components/atoms/Modal';
import { SvyIcon } from '@/components/savory/SvyIcon';
import { Form } from '@/core/form/Form';
import { SvyButton } from '@/components/savory/SvyButton';
import { FormGrid } from '@/components/atoms/Form';
import { Input } from '@/components/form/Input';
import { computeText } from '@/locales/utils';
import { AddressAutocomplete } from '@/pages/Restaurant/RestaurantInfo/components/AddressAutocomplete';
import { MapPreview } from '@/pages/Restaurant/RestaurantInfo/components/MapPreview';
import type { FormattedAddress } from '@/services/innovorder/pickupPoint/pickupPoint.type';
import { FISCAL_FIELDS_BY_COUNTRY } from '../OperationZones.type';
import ProviderSelector from '../ProviderSelector/ProviderSelector';
import {
    BackButton,
    DeviceCard,
    DeviceCardHeader,
    DeviceCardTitle,
    DeviceFieldsRow,
    Divider,
    MiddlewareSection,
    ModalHeaderFlag,
    ModalHeaderText,
    ModalSubtitle,
    ModalTitle,
    SectionHeader,
    SectionTitle,
} from './ConfigureZoneModal.style';
import type { AddressDetails, ConfigureZoneFormValues, ConfigureZoneModalProps } from './ConfigureZoneModal.type';

const FORM_ID = 'configure-zone-form';

const FormResetter: React.FunctionComponent<{
    values: ConfigureZoneFormValues;
    reset: (values: ConfigureZoneFormValues) => void;
}> = ({ values, reset }) => {
    React.useEffect(() => {
        reset(values);
    }, [values, reset]);
    return null;
};

const EMPTY_ADDRESS: AddressDetails = {
    route: '',
    postalCode: '',
    locality: '',
    country: '',
    streetNumber: '',
    administrativeAreaLevel1: '',
    administrativeAreaLevel2: '',
    provider: '',
    providerId: '',
    lat: 0,
    lng: 0,
};

const EMPTY_VALUES: ConfigureZoneFormValues = {
    socialReason: '',
    addressName: '',
    address: EMPTY_ADDRESS,
    vatNumber: '',
    siret: '',
    fiscalRepresentative: '',
    deviceFiscalityConfigs: [],
};

const ConfigureZoneModal: React.FunctionComponent<ConfigureZoneModalProps> = ({
    isOpen,
    onHide,
    territory,
    defaultValues,
    onSubmit,
    availableProviders,
    countryCode,
    devices,
    existingDeviceFiscalityConfigs,
}) => {
    const intl = useIntl();
    const isMultiProvider = availableProviders.length > 1;
    const [selectedProvider, setSelectedProvider] = useState<string | null>(
        isMultiProvider ? null : availableProviders[0] ?? null,
    );

    const fiscalFields = FISCAL_FIELDS_BY_COUNTRY[countryCode] ?? FISCAL_FIELDS_BY_COUNTRY.FR;

    const initialValues = useMemo(() => {
        const configs = devices.map((device) => {
            const existing = existingDeviceFiscalityConfigs?.find((c) => c.deviceId === device.deviceId);
            return {
                deviceId: device.deviceId,
                cashboxId: existing?.cashboxId ?? '',
                accessToken: existing?.accessToken ?? '',
                countryCode: existing?.countryCode ?? countryCode,
            };
        });
        return { ...EMPTY_VALUES, ...defaultValues, deviceFiscalityConfigs: configs };
    }, [defaultValues, devices, existingDeviceFiscalityConfigs, countryCode]);

    const handleHide = useCallback(() => {
        setSelectedProvider(isMultiProvider ? null : availableProviders[0] ?? null);
        onHide();
    }, [isMultiProvider, availableProviders, onHide]);

    const handleBack = useCallback(() => {
        setSelectedProvider(null);
    }, []);

    const handleFormSubmit = useCallback(
        (values: ConfigureZoneFormValues) => {
            return onSubmit(values, selectedProvider ?? 'NONE');
        },
        [onSubmit, selectedProvider],
    );

    const isFiskaltrust = selectedProvider === 'FISKALTRUST';

    // Step 1: Provider selection
    if (isMultiProvider && selectedProvider === null) {
        return (
            <Modal isOpen={isOpen} onHide={handleHide} width={680}>
                <Modal.Header>
                    <ModalHeaderFlag>{territory.flag}</ModalHeaderFlag>
                    <ModalHeaderText>
                        <ModalTitle>
                            <FormattedMessage
                                id="operationZones.configModal.title"
                                values={{ zoneName: territory.name }}
                            />
                        </ModalTitle>
                        <ModalSubtitle>
                            <FormattedMessage
                                id="operationZones.configModal.subtitle"
                                values={{ code: territory.code }}
                            />
                        </ModalSubtitle>
                    </ModalHeaderText>
                </Modal.Header>
                <Modal.Body>
                    <ProviderSelector
                        availableProviders={availableProviders}
                        value={selectedProvider}
                        onChange={setSelectedProvider}
                    />
                </Modal.Body>
            </Modal>
        );
    }

    // Step 2: Configuration form
    return (
        <Form<ConfigureZoneFormValues> defaultValues={initialValues} mode="onChange">
            {({ handleSubmit, control, setValue, watch, formState, reset }) => (
                <>
                    <FormResetter values={initialValues} reset={reset} />
                    <Modal
                        isOpen={isOpen}
                        onHide={handleHide}
                        width={680}
                        buttons={
                            <SvyButton
                                type="submit"
                                form={FORM_ID}
                                color="destructive"
                                isLoading={formState.isSubmitting}
                                disabled={
                                    !formState.isValid ||
                                    formState.isValidating ||
                                    formState.isSubmitting ||
                                    !formState.isDirty
                                }
                            >
                                <SvyIcon icon="save-line" />
                                <FormattedMessage id="operationZones.configModal.save" />
                            </SvyButton>
                        }
                    >
                        <Modal.Header>
                            <ModalHeaderFlag>{territory.flag}</ModalHeaderFlag>
                            <ModalHeaderText>
                                <ModalTitle>
                                    <FormattedMessage
                                        id="operationZones.configModal.title"
                                        values={{ zoneName: territory.name }}
                                    />
                                </ModalTitle>
                                <ModalSubtitle>
                                    <FormattedMessage
                                        id="operationZones.configModal.subtitle"
                                        values={{ code: territory.code }}
                                    />
                                </ModalSubtitle>
                            </ModalHeaderText>
                        </Modal.Header>

                        <Modal.Body>
                            {isMultiProvider && (
                                <BackButton type="button" onClick={handleBack}>
                                    <SvyIcon icon="arrow-left-line" />
                                    <FormattedMessage id="operationZones.configModal.back" />
                                </BackButton>
                            )}

                            <form id={FORM_ID} onSubmit={handleSubmit(handleFormSubmit)}>
                                <SectionHeader>
                                    <SvyIcon icon="building-line" />
                                    <SectionTitle>
                                        <FormattedMessage id="operationZones.configModal.entitySection" />
                                    </SectionTitle>
                                </SectionHeader>

                                <FormGrid>
                                    <div data-columns="2">
                                        <Input
                                            labelId="operationZones.configModal.socialReason"
                                            name="socialReason"
                                            placeholder={computeText(
                                                intl,
                                                'operationZones.configModal.socialReason.placeholder',
                                            )}
                                        />
                                    </div>
                                    <div data-columns="2">
                                        <Controller
                                            name="addressName"
                                            control={control}
                                            rules={{ required: computeText(intl, 'field.required') }}
                                            render={({ field: { value } }) => (
                                                <AddressAutocomplete
                                                    value={value || ''}
                                                    labelId="operationZones.configModal.address"
                                                    onChange={(place: FormattedAddress) => {
                                                        setValue('addressName', place.name, {
                                                            shouldValidate: true,
                                                            shouldDirty: true,
                                                        });
                                                        setValue(
                                                            'address',
                                                            {
                                                                route: place.route,
                                                                postalCode: place.postalCode,
                                                                locality: place.locality,
                                                                country: place.country,
                                                                streetNumber: place.streetNumber,
                                                                administrativeAreaLevel1:
                                                                    place.administrativeAreaLevel1,
                                                                administrativeAreaLevel2:
                                                                    place.administrativeAreaLevel2,
                                                                provider: place.provider,
                                                                providerId: place.providerId,
                                                                lat: place.lat,
                                                                lng: place.lng,
                                                            },
                                                            { shouldDirty: true },
                                                        );
                                                    }}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div data-columns="2">
                                        <MapPreview lat={watch('address.lat') || 0} lng={watch('address.lng') || 0} />
                                    </div>
                                    <Input
                                        labelId="operationZones.configModal.postalCode"
                                        name="address.postalCode"
                                        readOnly
                                        rules={{ required: computeText(intl, 'field.required') }}
                                    />
                                    <Input
                                        labelId="operationZones.configModal.city"
                                        name="address.locality"
                                        readOnly
                                        rules={{ required: computeText(intl, 'field.required') }}
                                    />
                                    <Input
                                        labelId={fiscalFields.taxLabel}
                                        name={fiscalFields.taxFieldName}
                                        placeholder={computeText(intl, fiscalFields.taxPlaceholder)}
                                        rules={{ required: computeText(intl, 'field.required') }}
                                    />
                                    <Input
                                        labelId={fiscalFields.secondLabel}
                                        name={fiscalFields.secondFieldName}
                                        placeholder={computeText(intl, fiscalFields.secondPlaceholder)}
                                        rules={{ required: computeText(intl, 'field.required') }}
                                    />
                                </FormGrid>

                                {isFiskaltrust && devices.length > 0 && (
                                    <>
                                        <Divider />
                                        <SectionHeader>
                                            <SvyIcon icon="computer-line" />
                                            <SectionTitle>
                                                <FormattedMessage id="operationZones.configModal.middlewareSection" />
                                            </SectionTitle>
                                        </SectionHeader>
                                        <MiddlewareSection>
                                            {devices.map((device, index) => (
                                                <DeviceCard key={device.deviceId}>
                                                    <DeviceCardHeader>
                                                        <SvyIcon icon="terminal-box-line" />
                                                        <DeviceCardTitle>{device.deviceName}</DeviceCardTitle>
                                                    </DeviceCardHeader>
                                                    <DeviceFieldsRow>
                                                        <Input
                                                            labelId="operationZones.configModal.cashboxId"
                                                            name={`deviceFiscalityConfigs.${index}.cashboxId`}
                                                            placeholder={computeText(
                                                                intl,
                                                                'operationZones.configModal.cashboxId.placeholder',
                                                            )}
                                                            rules={{ required: computeText(intl, 'field.required') }}
                                                        />
                                                        <Input
                                                            labelId="operationZones.configModal.accessToken"
                                                            name={`deviceFiscalityConfigs.${index}.accessToken`}
                                                            placeholder={computeText(
                                                                intl,
                                                                'operationZones.configModal.accessToken.placeholder',
                                                            )}
                                                            rules={{ required: computeText(intl, 'field.required') }}
                                                        />
                                                    </DeviceFieldsRow>
                                                </DeviceCard>
                                            ))}
                                        </MiddlewareSection>
                                    </>
                                )}
                            </form>
                        </Modal.Body>
                    </Modal>
                </>
            )}
        </Form>
    );
};

export default ConfigureZoneModal;
